/**
 * SDK — Shared UI components and design tokens for editors and future plugins.
 *
 * Two ways to consume:
 *
 * 1. Named imports (for internal editors):
 *    import { DarkenSlider, SliderRow, ToggleSwitch } from '../../sdk';
 *
 * 2. PluginSDK object (for plugins — stable contract):
 *    export function Editor({ config, onChange, sdk }) {
 *      return <sdk.controls.Slider label="Waviness" ... />;
 *    }
 */

import React from 'react';
import { cn } from '../lib/utils';

// ─── Controls ───────────────────────────────────────────────────
import { DarkenSlider } from './controls/DarkenSlider';
import { ColorSwatchGrid } from './controls/ColorSwatchGrid';
import { LevelPicker } from './controls/LevelPicker';
import { SliderRow } from './controls/SliderRow';
import { PresetGrid } from './controls/PresetGrid';
import { ToggleSwitch } from './controls/ToggleSwitch';
import { CollapseTransition, useDelayedFalse } from './controls/CollapseTransition';
import { PositionGrid } from './controls/PositionGrid';
import { TextInput } from './controls/TextInput';
import { NumberInput } from './controls/NumberInput';
import { FileUpload } from './controls/FileUpload';
import { Button } from './controls/Button';
import { Spinner } from './controls/Spinner';
import { ScopeSelector } from './controls/ScopeSelector';
import { FieldInput } from './controls/FieldInput';
import { LogoUpload } from './controls/LogoUpload';

// ─── Design Tokens ──────────────────────────────────────────────
import {
  TOGGLE_SIZES,
  SLIDER_THUMB_CLS,
  RANGE_SLIDER_CLS,
  STEPPED_SLIDER_CLS,
  TEXT,
  BUTTON_ACTIVE,
  BUTTON_INACTIVE,
  SWATCH_ACTIVE,
  SWATCH_INACTIVE,
} from './constants';

// ─── Shared Data Constants (re-exported for convenience) ────────
import { BG_DARKEN_LEVELS } from '../constants/backgrounds';
import {
  GRADIENT_BASE_TONES,
  GRADIENT_ACCENT_COLORS,
  GRADIENT_FOG_LEVELS,
} from '../constants/backgrounds';

// ─── Named Exports (internal usage) ────────────────────────────
// Controls (canonical names)
export { DarkenSlider };
export { ColorSwatchGrid };
export { LevelPicker };
export { SliderRow };
export { PresetGrid };
export { ToggleSwitch };
export { CollapseTransition, useDelayedFalse };
export { PositionGrid };
export { TextInput };
export { NumberInput };
export { FileUpload };
export { Button };
export { Spinner };
export { ScopeSelector };
export { FieldInput };
export { LogoUpload };

// Aliases matching Plugin SDK contract names
export { SliderRow as Slider };
export { ColorSwatchGrid as ColorPicker };
export { ColorSwatchGrid as BaseTonePicker };

// Design tokens
export {
  TOGGLE_SIZES,
  SLIDER_THUMB_CLS,
  RANGE_SLIDER_CLS,
  STEPPED_SLIDER_CLS,
  TEXT,
  BUTTON_ACTIVE,
  BUTTON_INACTIVE,
  SWATCH_ACTIVE,
  SWATCH_INACTIVE,
};

// ─── PluginSDK Object (plugin contract — §5 of vision doc) ─────
export const PluginSDK = {
  // React & utilities
  React,
  cn,

  // Shared design data
  colors: {
    baseTones: GRADIENT_BASE_TONES,
    accents: GRADIENT_ACCENT_COLORS,
  },
  levels: {
    darken: BG_DARKEN_LEVELS,
    fog: GRADIENT_FOG_LEVELS,
  },

  // Reusable UI components — these ensure plugins look native
  controls: {
    ToggleSwitch,
    CollapseTransition,
    useDelayedFalse,
    DarkenSlider,
    Slider: SliderRow,
    ColorPicker: ColorSwatchGrid,
    BaseTonePicker: ColorSwatchGrid,
    LevelPicker,
    PresetGrid,
    PositionGrid,
    TextInput,
    NumberInput,
    FileUpload,
    Button,
    Spinner,
    ScopeSelector,
    FieldInput,
    LogoUpload,
  },

  // Design tokens
  tokens: {
    TOGGLE_SIZES,
    TEXT,
    BUTTON_ACTIVE,
    BUTTON_INACTIVE,
    SWATCH_ACTIVE,
    SWATCH_INACTIVE,
  },
};
